import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, RefreshCw, Info, CheckCircle, AlertCircle } from "lucide-react";
import ticketService from "../../services/ticketService";
import websiteService from "../../services/websiteService";
import SyncLoadingOverlay from "../../components/SyncLoadingOverlay";
import Toast from "../../components/Toast";
import { useMetaPixel } from "../../hooks/use-meta-pixel";
import { useGoogleAnalytics } from "../../hooks/use-google-analytics";
import { useGoogleAds } from "../../hooks/use-google-ads";

const NewTicket = () => {
  const navigate = useNavigate();
  const { trackSupportTicket } = useMetaPixel();
  const { trackSupportTicket: trackGASupportTicket } = useGoogleAnalytics();
  const { trackSupportTicket: trackAdsSupportTicket } = useGoogleAds();

  // Form state
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    website_id: "",
    page_id: "",
    website_root_url: "", // Added website_root_url to formData
  });
  
  // Component state
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [websites, setWebsites] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch user websites
  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        setLoading(true);
        const response = await websiteService.getUserWebsites();
        
        if (response && response.success) {
          setWebsites(response.websites || []);
        }
      } catch (err) {
        console.error("Error fetching websites:", err);
        setToast({
          show: true,
          message: "Failed to load your websites. Please try again later.",
          type: "error"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWebsites();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === "website_id") {
        // Parse website_id value to integer for comparison
        const selectedWebsite = websites.find(website => website.id === parseInt(value, 10));
        return { 
          ...prev, 
          [name]: value, 
          website_root_url: selectedWebsite ? selectedWebsite.root_url : "" 
        };
      }
      return { ...prev, [name]: value };
    });
    
    // Clear validation error when field is updated
    if (validationErrors[name]) {
      setValidationErrors(prev => ({...prev, [name]: null}));
    }
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.subject.trim()) {
      errors.subject = "Subject is required";
    }
    
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }
    
    if (!formData.website_id) {
      errors.website_id = "Please select a website";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate the form before submission
    if (!validateForm()) {
      setToast({
        show: true,
        message: "Please fill in all required fields",
        type: "error"
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Submit form
      const response = await ticketService.createTicket(formData);
      
      if (!response || !response.success) {
        throw new Error(response?.message || "Failed to create ticket");
      }
      
      // Track support ticket creation
      trackSupportTicket();

      // Track support ticket creation with Google Analytics
      trackGASupportTicket();

      // Track support ticket creation with Google Ads
      trackAdsSupportTicket({
        type: formData.subject ? 'specific' : 'general',
        priority: 'normal'
      });
      
      // Show success notification
      setToast({
        show: true,
        message: "Ticket created successfully",
        type: "success"
      });
      
      // Navigate to the created ticket
      setTimeout(() => {
        navigate(`/dashboard/tickets/${response.ticket.id}`);
      }, 1500);
    } catch (err) {
      console.error("Error creating ticket:", err);
      setToast({
        show: true,
        message: err.message || "Failed to create ticket",
        type: "error"
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      {/* Toast notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/dashboard/tickets"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-4 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to tickets
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-800">Create New Support Ticket</h1>
        <p className="text-gray-600 mt-2">
          Submit a new support request and our team will assist you as soon as possible.
        </p>
      </div>
      
      {/* Main content layout - 2 column on larger screens */}
      <div className="grid md:grid-cols-3 gap-8">
        {/* Left column - Form */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 relative">
            {loading && <SyncLoadingOverlay 
              subtle={true} 
              message="Loading your websites..." 
              subMessage="Please wait while we fetch your data" 
            />}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Subject field */}
              <div>
                <label 
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className={`block w-full rounded-md border ${validationErrors.subject ? 'border-red-500' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50`}
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={handleChange}
                />
                {validationErrors.subject && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.subject}</p>
                )}
              </div>
              
              {/* Website selection */}
              <div>
                <label 
                  htmlFor="website_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Related Website <span className="text-red-500">*</span>
                </label>
                <select
                  id="website_id"
                  name="website_id"
                  className={`block w-full rounded-md border ${validationErrors.website_id ? 'border-red-500' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50`}
                  value={formData.website_id}
                  onChange={handleChange}
                >
                  <option value="">-- Select a website --</option>
                  {websites.map(website => (
                    <option key={website.id} value={website.id}>
                      {website.name} ({website.root_url})
                    </option>
                  ))}
                </select>
                {validationErrors.website_id && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.website_id}</p>
                )}
                {websites.length === 0 && !loading && (
                  <div className="mt-2 text-sm text-amber-600 flex items-start">
                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                    <span>No websites found. Please add a website first or contact support if you believe this is an error.</span>
                  </div>
                )}
              </div>
              
              {/* Message field */}
              <div>
                <label 
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="10"
                  className={`block w-full rounded-md border ${validationErrors.message ? 'border-red-500' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50`}
                  placeholder="Please describe your issue in detail..."
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
                {validationErrors.message && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.message}</p>
                )}
                <p className="text-sm text-gray-500 mt-1 flex items-start">
                  <Info className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>Include error messages, URLs, and steps to reproduce the issue for faster resolution.</span>
                </p>
              </div>
              
              {/* Submit button */}
              <div className="flex justify-end pt-4">
                <Link 
                  to="/dashboard/tickets"
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors mr-3"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-5 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        {/* Right column - Support info */}
        <div className="md:col-span-1">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-5">
            <h2 className="text-lg font-semibold mb-4 text-indigo-800 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
              Support Guidelines
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-indigo-700 mb-1">Response Times</h3>
                <p className="text-sm text-gray-700">
                  Our team responds within 1 hour or up to 24 hours after analyzing the issue, score, and credit cost.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-indigo-700 mb-1">Before Submitting</h3>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Search existing tickets for similar issues first</li>
                  <li>Include browser/device information (e.g., Chrome 122, iPhone 14, macOS Sonoma)</li>
                  <li>Specify the exact URL where issues occur</li>
                  <li>For screenshots, use services like Jumpshare, Lightshot, Snagit, or CleanShot X</li>
                  <li>For large attachments and files, share via Google Drive, OneDrive, or Dropbox links</li>
                  <li>For videos, use services like Loom, Screencast-O-Matic, or YouTube unlisted links</li>
                  <li>Include console errors if applicable (F12 → Console tab in most browsers)</li>
                </ul>
              </div>
              
              <div className="pt-2">
                <h3 className="font-medium text-indigo-700 mb-1">Support Availability</h3>
                <p className="text-sm text-gray-700">
                  Support is available 24/7. Priority is given to urgent production issues and maintenance requests.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTicket;